import { baseApi } from "@/shared/api";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type { SigningActionResponse } from "@/shared/types";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import type { SignDocumentDetails } from "@/features/self/pending/types/pending.types";
import { leavesApi } from "./leaves";

export const ordersApi = baseApi.injectEndpoints({
  endpoints(builder) {
    return {
      previewAnnualOrder: builder.mutation<FileResponse, number>({
        query(id) {
          return {
            url: `/api/hr/annual-leaves/${id}/preview-order/`,
            method: "GET",
            credentials: "include",
            responseHandler: fileResponseHandler,
          };
        },
      }),
      previewUnpaidOrder: builder.mutation<FileResponse, number>({
        query(id) {
          return {
            url: `/api/hr/unpaid-leaves/${id}/preview-order/`,
            method: "GET",
            credentials: "include",
            responseHandler: fileResponseHandler,
          };
        },
      }),
      previewMedicalOrder: builder.mutation<FileResponse, number>({
        query(id) {
          return {
            url: `/api/hr/medical-leaves/${id}/preview-order/`,
            method: "GET",
            credentials: "include",
            responseHandler: fileResponseHandler,
          };
        },
      }),
      signVacationOrder: builder.mutation<
        SigningActionResponse,
        { orderId: number; cms_signature: string; leaveId: number; leaveType: string }
      >({
        query({ orderId, cms_signature }) {
          return {
            url: `/api/hr/vacation-orders/${orderId}/sign/`,
            method: "POST",
            body: { cms_signature },
            credentials: "include",
          };
        },
        invalidatesTags() {
          return [
            { type: "LeaveApplications" as const, id: "LIST" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ leaveId, leaveType }, { dispatch, queryFulfilled }) {
          const pendingListPatch = dispatch(
            pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
              const sourceMap: Record<string, string> = { annual: "annual_leave", unpaid: "unpaid_leave" };
              const sourceType = sourceMap[leaveType];
              const idx = draft.items.findIndex(
                (item) =>
                  item.action_required === "sign_document" &&
                  "document_id" in item.details &&
                  (item.details as SignDocumentDetails).source_type === sourceType,
              );
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              }
            }),
          );
          const pendingCountPatch = dispatch(
            authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          );
          try {
            await queryFulfilled;
            const queryName = leaveType === "annual" ? "getAnnualLeave" as const : "getUnpaidLeave" as const;
            dispatch(
              leavesApi.util.updateQueryData(queryName, leaveId, (draft) => {
                draft.signing_status = 1;
                draft.status = "order_uploaded";
              }),
            );
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      signMedicalLeaveOrder: builder.mutation<
        SigningActionResponse,
        { orderId: number; cms_signature: string; leaveId: number }
      >({
        query({ orderId, cms_signature }) {
          return {
            url: `/api/hr/medical-leave-orders/${orderId}/sign/`,
            method: "POST",
            body: { cms_signature },
            credentials: "include",
          };
        },
        invalidatesTags() {
          return [
            { type: "LeaveApplications" as const, id: "LIST" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ leaveId }, { dispatch, queryFulfilled }) {
          const pendingListPatch = dispatch(
            pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
              const idx = draft.items.findIndex(
                (item) =>
                  item.action_required === "sign_document" &&
                  "document_id" in item.details &&
                  (item.details as SignDocumentDetails).source_type === "medical_leave",
              );
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              }
            }),
          );
          const pendingCountPatch = dispatch(
            authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          );
          try {
            await queryFulfilled;
            dispatch(
              leavesApi.util.updateQueryData("getMedicalLeave", leaveId, (draft) => {
                draft.signing_status = 1;
                draft.status = "order_uploaded";
              }),
            );
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      uploadMedicalCertificate: builder.mutation<
        { success: boolean; message: string; data: { id: number; certificate_pdf_key: string; certificate_uploaded_at: string } },
        { id: number; file: File }
      >({
        query({ id, file }) {
          const formData = new FormData();
          formData.append("certificate", file);
          return {
            url: `/api/hr/medical-leaves/${id}/upload-certificate/`,
            method: "POST",
            body: formData,
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
          ];
        },
      }),
    };
  },
});

export const {
  usePreviewAnnualOrderMutation,
  usePreviewUnpaidOrderMutation,
  usePreviewMedicalOrderMutation,
  useSignVacationOrderMutation,
  useSignMedicalLeaveOrderMutation,
  useUploadMedicalCertificateMutation,
} = ordersApi;

